import { normalizeQueryAndExclude, quoteIfNeeded, splitQuery, uniqBy } from '@/lib/search-utils'

type Tag = {
  id?: number
  group: string | null
  name: string
}

type CustomSearchParams = {
  query?: string | null
  exclude?: string | null
  tags?: Tag[]
  excludeTags?: Tag[]
  customerId?: number | null
  verticalDivisionId?: number | null
  lateralDivisionId?: number | null
  createdBy?: number | null
  salesRecordingGroupId?: number | null
  opportunityStatusIds?: number[]
  incentiveFilter?: 'target' | 'disabled' | null
  opportunityTypeId?: number | null
}

export class CustomSearchCaptain {
  private $query: string[] = []
  private $exclude: string[] = []
  tags: Tag[] = []
  excludeTags: Tag[] = []
  customerId: number | null = null
  verticalDivisionId: number | null = null
  lateralDivisionId: number | null = null
  createdBy: number | null = null
  salesRecordingGroupId: number | null = null
  opportunityStatusIds: number[] = []
  incentiveFilter: 'target' | 'disabled' | null = null
  opportunityTypeId: number | null = null

  constructor(args?: CustomSearchParams) {
    this.init(args)
  }

  init(args?: CustomSearchParams) {
    this.$query = []
    this.$exclude = []
    this.tags = []
    this.excludeTags = []
    this.customerId = null
    this.verticalDivisionId = null
    this.lateralDivisionId = null
    this.createdBy = null
    this.salesRecordingGroupId = null
    this.opportunityStatusIds = []
    this.incentiveFilter = null
    this.opportunityTypeId = null

    if (!args || !Object.keys(args).length) return

    if (args.query) this.query = args.query
    if (args.exclude) this.exclude = args.exclude
    if (args.tags) this.tags = args.tags
    if (args.excludeTags) this.excludeTags = args.excludeTags
    if (args.customerId) this.customerId = args.customerId
    if (args.verticalDivisionId) this.verticalDivisionId = args.verticalDivisionId
    if (args.lateralDivisionId) this.lateralDivisionId = args.lateralDivisionId
    if (args.createdBy) this.createdBy = args.createdBy
    if (args.salesRecordingGroupId) this.salesRecordingGroupId = args.salesRecordingGroupId
    if (args.opportunityStatusIds) this.opportunityStatusIds = args.opportunityStatusIds
    if (args.incentiveFilter) this.incentiveFilter = args.incentiveFilter
    if (args.opportunityTypeId) this.opportunityTypeId = args.opportunityTypeId

    this.normalize()
  }

  static build(args?: CustomSearchParams): CustomSearchCaptain {
    return new CustomSearchCaptain(args)
  }

  set query(q: string | null) {
    if (q) {
      this.$query = splitQuery(q, { unquote: true })
    }
  }

  get queries(): string[] {
    return this.$query
  }

  get excludes(): string[] {
    return this.$exclude
  }

  get query(): string {
    return this.$query.map(quoteIfNeeded).join(' ')
  }

  set exclude(q: string | null) {
    if (q) {
      this.$exclude = splitQuery(q, { unquote: true })
    }
  }

  get exclude(): string {
    return this.$exclude.map(quoteIfNeeded).join(' ')
  }

  enabled(): boolean {
    return !!(
      this.$query.length ||
      this.$exclude.length ||
      this.tags.length ||
      this.excludeTags.length ||
      this.customerId ||
      this.verticalDivisionId ||
      this.lateralDivisionId ||
      this.createdBy ||
      this.salesRecordingGroupId ||
      this.opportunityStatusIds.length ||
      this.incentiveFilter ||
      this.opportunityTypeId
    )
  }

  normalize() {
    const result = normalizeQueryAndExclude(this.$query, this.$exclude)
    this.$query = result.queries
    this.$exclude = result.excludes
    this.tags = uniqBy(this.tags, (v) => (v.group ? `${v.group}:${v.name}` : v.name))
    this.excludeTags = uniqBy(this.excludeTags, (v) => (v.group ? `${v.group}:${v.name}` : v.name))
  }

  stringify(): string {
    this.normalize()

    const results: string[] = []
    for (const v of this.$query) {
      results.push(quoteIfNeeded(v))
    }
    for (const v of this.$exclude) {
      results.push(`-${quoteIfNeeded(v)}`)
    }

    if (this.tags.length)
      this.tags.forEach((v) => {
        results.push(v.group ? `tag:${v.group}:${v.name}` : `tag:${v.name}`)
      })

    if (this.excludeTags.length)
      this.excludeTags.forEach((v) => {
        results.push(v.group ? `exclude_tag:${v.group}:${v.name}` : `exclude_tag:${v.name}`)
      })

    if (this.customerId) results.push(`customer:${this.customerId}`)
    if (this.verticalDivisionId) results.push(`vertical_division:${this.verticalDivisionId}`)
    if (this.lateralDivisionId) results.push(`lateral_division:${this.lateralDivisionId}`)
    if (this.createdBy) results.push(`created_by:${this.createdBy}`)
    if (this.salesRecordingGroupId) results.push(`sales_recording_group:${this.salesRecordingGroupId}`)

    if (this.opportunityStatusIds.length) {
      this.opportunityStatusIds.forEach((id) => {
        results.push(`opportunity_status_id:${id}`)
      })
    }

    if (this.incentiveFilter) {
      results.push(`incentive_filter:${this.incentiveFilter}`)
    }

    if (this.opportunityTypeId) {
      results.push(`opportunity_type:${this.opportunityTypeId}`)
    }

    return results.join(' ')
  }

  parse(query: string): this {
    this.init()
    if (!query) return this

    const queries = splitQuery(query, { unquote: true })

    queries.forEach((v) => {
      if (v.startsWith('tag:')) {
        const parts = v.split(/:/)
        const tagGroupOrName = parts[1] || ''
        const tagName = parts[2]
        if (tagName) {
          this.tags.push({
            name: tagName,
            group: tagGroupOrName,
          })
        } else if (tagGroupOrName) {
          this.tags.push({
            name: tagGroupOrName,
            group: null,
          })
        }
        return
      }

      if (v.startsWith('exclude_tag:')) {
        const parts = v.split(/:/)
        const tagGroupOrName = parts[1] || ''
        const tagName = parts[2]
        if (tagName) {
          this.excludeTags.push({
            name: tagName,
            group: tagGroupOrName,
          })
        } else if (tagGroupOrName) {
          this.excludeTags.push({
            name: tagGroupOrName,
            group: null,
          })
        }
        return
      }

      if (v.startsWith('customer:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.customerId = Number(value)
        return
      }

      if (v.startsWith('vertical_division:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.verticalDivisionId = Number(value)
        return
      }

      if (v.startsWith('lateral_division:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.lateralDivisionId = Number(value)
        return
      }

      if (v.startsWith('created_by:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.createdBy = Number(value)
        return
      }

      if (v.startsWith('sales_recording_group:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.salesRecordingGroupId = Number(value)
        return
      }

      if (v.startsWith('opportunity_status_id:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (!Array.isArray(this.opportunityStatusIds)) {
          this.opportunityStatusIds = []
        }
        if (value) this.opportunityStatusIds.push(Number(value))
        return
      }

      if (v.startsWith('incentive_filter:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.incentiveFilter = value as 'target' | 'disabled'
        return
      }

      if (v.startsWith('opportunity_type:')) {
        const parts = v.split(/:/)
        const value = parts[1]
        if (value) this.opportunityTypeId = Number(value)
        return
      }

      this.$query.push(v)
    })

    this.normalize()
    return this
  }
}
